import depsloader from "../../../depsloader";

const SEARCH_URL = "https://api.screenwork.de/geocoder/search";
const REVERSE_URL = "https://api.screenwork.de/geocoder/reverse";
const REQUEST_TIMEOUT = 20000;

function mapsKey() {
  const el = typeof document !== "undefined" ? document.documentElement : null;
  const key = el && el.dataset ? el.dataset.mapsKey : "";
  return typeof key === "string" ? key.trim() : "";
}

function isFiniteNumber(v) {
  return typeof v === "number" && isFinite(v);
}

function validLatLng(lat, lng) {
  return isFiniteNumber(lat) && isFiniteNumber(lng) &&
    lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
}

function str(v) {
  return typeof v === "string" && v.trim() !== "" ? v : null;
}

class Geocoder {
  constructor(axios) {
    this.axios = axios;
    this.axiosRequest = null;
    this.reverseRequest = null;
    this.seq = 0;
    this.reverseSeq = 0;
  }

  cancelAutocomplete() {
    // invalidiert auch Antworten, die bereits unterwegs sind
    this.seq++;
    if (this.axiosRequest != null) {
      this.axiosRequest.source.cancel();
      this.axiosRequest = null;
    }
  }

  cancelReverse() {
    this.reverseSeq++;
    if (this.reverseRequest != null) {
      this.reverseRequest.source.cancel();
      this.reverseRequest = null;
    }
  }

  _geoaddressText(g) {
    let s = "";
    if (g.zip && g.city && g.district) {
      s = g.zip + " " + g.city + "–" + g.district;
    } else if (g.zip && g.city) {
      s = g.zip + " " + g.city;
    } else if (g.city && g.district) {
      s = g.city + "–" + g.district;
    } else if (g.city) {
      s = g.city;
    }

    const p = [];

    if (g.street && g.number) {
      p.push(g.street + " " + g.number);
    } else if (g.street) {
      p.push(g.street);
    }

    if (s) {
      p.push(s);
    }

    if (g.state && !g.street) {
      p.push(g.state);
    }

    if (g.country && !g.street) {
      p.push(g.country);
    }

    return p.join(", ");
  }

  // Liefert ein neues Objekt; das Feature wird nicht verändert.
  _geoaddress(l) {
    if (!l || typeof l !== "object") {
      return null;
    }

    const coords = l.geometry && Array.isArray(l.geometry.coordinates) ? l.geometry.coordinates : null;
    if (!coords || coords.length < 2 || !validLatLng(coords[1], coords[0])) {
      return null;
    }

    const p = l.properties && typeof l.properties === "object" ? l.properties : {};
    const type = str(p.type);
    const name = str(p.name);

    const geoaddress = {};
    let street = str(p.street);
    let city = str(p.city);
    let district = str(p.district);

    if (type) {
      if (type === "street" && !street) {
        street = name;
      } else if (type === "city" && !city) {
        city = name;
      } else if (type === "district" && !district) {
        district = name;
      }
    } else {
      // Legacy-Fallback für Antworten ohne properties.type
      if (p.osm_key == "highway" && name) {
        street = name;
      }
      const isDistrict = p.osm_key == "boundary" || (p.osm_key == "place" && p.osm_value == "suburb");
      if (isDistrict && name && name !== city && !district) {
        district = name;
      }
      if (p.osm_key == "place" && ["city", "town", "village"].indexOf(p.osm_value) > -1 && !city) {
        city = name;
      }
    }

    if (street) {
      geoaddress["street"] = street;
      if (str(p.housenumber)) {
        geoaddress["number"] = p.housenumber;
      }
    }

    if (str(p.postcode)) {
      geoaddress["zip"] = p.postcode;
    }
    if (city) {
      geoaddress["city"] = city;
    }
    if (district && district !== city) {
      geoaddress["district"] = district;
    }

    geoaddress["lat"] = coords[1];
    geoaddress["lng"] = coords[0];

    geoaddress["state"] = p.state;
    geoaddress["country"] = p.country;

    let text = this._geoaddressText(geoaddress);
    if (!city && !street && name) {
      // Feature-Name nur als Beschriftung, nicht als Stadt
      const rest = text;
      text = rest ? name + ", " + rest : name;
    }
    geoaddress["text"] = text;

    if (!geoaddress["text"]) {
      return null;
    }

    return geoaddress;
  }

  // Löst mit einem Array auf, mit null bei abgebrochener oder überholter
  // Anfrage; lehnt nur bei echten Fehlern ab.
  autocomplete(q) {
    this.cancelAutocomplete();
    const seq = this.seq;

    const query = typeof q === "string" ? q.trim() : "";
    if (!query) {
      return Promise.resolve([]);
    }

    const key = mapsKey();
    if (!key) {
      console.warn("Geocoder: kein Maps-Key vorhanden");
      return Promise.resolve([]);
    }

    const source = this.axios.CancelToken.source();
    const request = { source };
    this.axiosRequest = request;

    const params = new URLSearchParams();
    params.append("q", query);
    params.append("lang", "de");
    params.append("limit", "10");
    params.append("key", key);

    return this.axios.get(SEARCH_URL, {
      cancelToken: source.token,
      timeout: REQUEST_TIMEOUT,
      params
    }).then(res => {
      if (seq !== this.seq) {
        return null;
      }
      const features = res && res.data && Array.isArray(res.data.features) ? res.data.features : [];
      const results = [];
      features.forEach(l => {
        const geoaddress = this._geoaddress(l);
        if (geoaddress) {
          results.push(geoaddress);
        }
      });
      return results;
    }, thrown => {
      if (this.axios.isCancel(thrown) || seq !== this.seq) {
        return null;
      }
      console.warn("Geocoder: Suche fehlgeschlagen", thrown && thrown.message);
      throw thrown;
    }).finally(() => {
      if (this.axiosRequest === request) {
        this.axiosRequest = null;
      }
    });
  }

  // Löst mit einer Adresse auf, lehnt bei Fehlern, fehlendem Key,
  // ungültigen Koordinaten oder Abbruch ab.
  reverse(lat, lng) {
    this.cancelReverse();
    const seq = this.reverseSeq;

    lat = Number(lat);
    lng = Number(lng);
    if (!validLatLng(lat, lng)) {
      return Promise.reject(new Error("invalid coordinates"));
    }

    const key = mapsKey();
    if (!key) {
      return Promise.reject(new Error("maps key missing"));
    }

    const source = this.axios.CancelToken.source();
    const request = { source };
    this.reverseRequest = request;

    const params = new URLSearchParams();
    params.append("lat", String(lat));
    params.append("lon", String(lng));
    params.append("lang", "de");
    params.append("key", key);

    return this.axios.get(REVERSE_URL, {
      cancelToken: source.token,
      timeout: REQUEST_TIMEOUT,
      params
    }).then(res => {
      if (seq !== this.reverseSeq) {
        throw new Error("superseded");
      }
      const features = res && res.data && Array.isArray(res.data.features) ? res.data.features : [];
      const geoaddress = features.length > 0 ? this._geoaddress(features[0]) : null;
      if (!geoaddress) {
        throw new Error("invalid data");
      }
      return geoaddress;
    }).finally(() => {
      if (this.reverseRequest === request) {
        this.reverseRequest = null;
      }
    });
  }

  destroy() {
    this.cancelAutocomplete();
    this.cancelReverse();
  }
}

function GeocoderFactory() {
  return new Promise(resolve => {
    depsloader.load("axios").then(axios => {
      resolve(new Geocoder(axios));
    })
  });
}

export { GeocoderFactory };

export default {};
